import type { SasDecodedField, SasDecodedLine, SasDecodeResult, SasExchange } from '../../api/types';

/**
 * Colour roles for the decode stream. The CSS variable for a role is always
 * `--sas-{role}` — the renderer (`DecodeStream`) and this module never emit a
 * literal colour hex; only role names travel with the line model. Task 3 makes
 * the CSS-variable *values* user-configurable (per-user palette) without either
 * this module or the renderer changing.
 */
export type SasColorRole = 'tx' | 'rx' | 'text' | 'frame' | 'dim' | 'ok' | 'err';

/** One coloured run of text. Concatenating a line's spans' `text` (in order,
 * with no separator) reproduces exactly what that line exports as plain text. */
export type SasSpan = { text: string; role: SasColorRole };

/** One visual row of the stream: a sequence of spans rendered left-to-right.
 * An empty array (`[]`) is a blank spacer row (used between exchange boxes);
 * a single frame-role `│` span is a blank spacer row *inside* one exchange box
 * (between a request's decode-block and the next line's frame row). */
export type SasModelLine = SasSpan[];

/** Default Option-C palette — role -> hex. Task 3 seeds the per-user colour
 * dialog with these values and Reset-to-default restores them; the same role
 * keys are what gets stored in `preferences.sasAnalyzer.colors`. */
export const DEFAULT_SAS_COLORS: Record<SasColorRole, string> = {
  tx: '#ff9e4a',
  rx: '#6ca8ff',
  text: '#e8edf3',
  frame: '#7d8794',
  dim: '#9aa5b1',
  ok: '#3fd68f',
  err: '#ff6b6b',
};

/** Width the `HH:MM:SS.mmm` timestamp column is padded to. */
export const TS_WIDTH = 12;
/** Raw-hex bytes per physical line before wrapping to a continuation line. */
export const BYTES_PER_WRAP_LINE = 16;
/** Minimum exchange-box width (in characters), regardless of content. */
export const MIN_BOX_WIDTH = 44;

/** Column (0-indexed, counting the leading `│`) decode content — the summary,
 * field, and warning rows beneath a TX/RX frame line — starts at. Lines up
 * under the frame line's TX/RX direction label, per the approved mockup
 * (`analyser-mockups.html`, "Option C (revised)" + its `.txt` export section). */
export const DECODE_INDENT = 16;

/** Minimum width (from `DECODE_INDENT`) of the label column for a field that
 * carries a bit-reference annotation, and the minimum width of the value
 * column that follows it — both widened together, per exchange, to fit the
 * widest label/value actually present so the bit-ref column stays aligned
 * across every field in that exchange ("one column for the whole ... block").
 * The floors reproduce the mockup's Features block (label col 30, value col
 * 17, bit-ref col 16+30+17=63) exactly for equivalent-sized content. */
export const FIELD_LABEL_FLOOR = 30;
export const FIELD_VALUE_FLOOR = 17;
/** Small fixed gap between a non-numeric, non-bit-ref field's label and value
 * (e.g. a raw-byte-plus-binary bitmap header) — these aren't aligned to a
 * shared column since nothing follows them on the line. */
const TEXT_FIELD_GAP = 3;
/** Minimum total width (from `DECODE_INDENT`) of a numeric field's
 * label+value region; the value is right-aligned to the end of it. Widened
 * per exchange to fit the widest label/value, floor matches the mockup's
 * meters block (label+value = 34, value right-aligned to col 16+34=50). */
export const NUMERIC_TOTAL_FLOOR = 34;

// A value reads as numeric (and is right-aligned) when it's digits, optional
// thousands separators, and an optional decimal fraction — matches everything
// FieldReader.Amount/Bcd emit (e.g. "1,234", "5000.00"), never "Enabled"/"Yes".
const NUMERIC_VALUE_RE = /^-?\d[\d,]*(\.\d+)?$/;

// A field's rawHex reads as a bit-reference annotation (vs. e.g. a "1.3"
// byte.bit pair, which is NOT treated as one) when it's literally "bit N" or
// "bits N-M" / "bits N–M" (en-dash), per FieldReader.Bit/BitField/BitRange.
const BIT_REF_RE = /^bits?\s+\d+(\s*[-–]\s*\d+)?$/i;

// A decoded value is "good" (ok role) unless it's one of the codebase's known
// not-a-real-value placeholders (FieldReader.Missing / FieldReader.Bcd's
// invalid-BCD fallback), the default BitSpec.Flag "off" text (explicitly
// called out: stays general text, not every custom on/off pair — see
// lineModel.test.ts for the rationale), or a raw hex/binary echo of the wire
// bytes rather than an actually-decoded value (FieldReader.Bits' raw-byte
// summary field, FieldReader.Byte's mapped "0xNN — meaning" value, or the
// PayloadRegistry raw-hex fallback on a decode exception).
const NEGATIVE_VALUE_RE = /^(disabled|missing \(short frame\))$/i;
const INVALID_BCD_RE = /^invalid bcd/i;
const RAW_HEX_LITERAL_RE = /0x[0-9a-f]{2}|0b[01]{4,}/i;
const HEX_BYTE_DUMP_RE = /^[0-9a-f]{2}( [0-9a-f]{2})*$/i;

function isNumericValue(value: string): boolean {
  return NUMERIC_VALUE_RE.test(value.trim());
}

function isBitRef(rawHex: string | null | undefined): boolean {
  return !!rawHex && BIT_REF_RE.test(rawHex.trim());
}

// Exported so commandSearch.ts's "Errors (CRC BAD / NACK)" dropdown entry
// matches exactly the same warnings this module colours err-role — one
// source of truth for "is this an error", not two regexes that could drift.
export function isErrorWarning(warning: string): boolean {
  return /nack|crc bad|decode error/i.test(warning);
}

// Numeric values skip the raw-hex/hex-dump checks entirely — a plain 2-digit
// meter value like "12" would otherwise false-positive against HEX_BYTE_DUMP_RE
// (every hex digit 0-9a-f includes every decimal digit).
function fieldValueRole(value: string, numeric: boolean): SasColorRole {
  const v = value.trim();
  if (NEGATIVE_VALUE_RE.test(v)) return 'text';
  if (INVALID_BCD_RE.test(v)) return 'text';
  if (!numeric && (RAW_HEX_LITERAL_RE.test(v) || HEX_BYTE_DUMP_RE.test(v))) return 'text';
  return 'ok';
}

function frameGutter(): SasSpan {
  return { text: '│ ', role: 'frame' };
}

// Wider gutter for summary/field/warning rows so their content lines up at
// DECODE_INDENT, under the TX/RX direction label on the frame line above.
function decodeGutter(): SasSpan {
  return { text: `│${' '.repeat(DECODE_INDENT - 1)}`, role: 'frame' };
}

// A blank row *inside* one exchange box, between one line's decode-block and
// the next line's frame row (e.g. between a request and its response) — just
// the bare gutter character, per the mockup's `.txt` export section.
function innerSpacer(): SasModelLine {
  return [{ text: '│', role: 'frame' }];
}

function lineLength(line: SasModelLine): number {
  return line.reduce((n, s) => n + s.text.length, 0);
}

function wrapHexTokens(rawHex: string): string[] {
  const tokens = rawHex.trim().length ? rawHex.trim().split(/\s+/) : [];
  if (tokens.length === 0) return [''];
  const rows: string[] = [];
  for (let i = 0; i < tokens.length; i += BYTES_PER_WRAP_LINE) {
    rows.push(tokens.slice(i, i + BYTES_PER_WRAP_LINE).join(' '));
  }
  return rows;
}

function dirLabel(direction: SasDecodedLine['direction']): 'TX' | 'RX' | '??' {
  if (direction === 'Tx') return 'TX';
  if (direction === 'Rx') return 'RX';
  return '??';
}

function dirRole(direction: SasDecodedLine['direction']): SasColorRole {
  if (direction === 'Tx') return 'tx';
  if (direction === 'Rx') return 'rx';
  return 'dim';
}

// `HH:MM:SS.mmm  TX  <raw hex>` — continuation rows (when the hex wraps) carry
// no timestamp/direction, just enough leading spaces to line the hex bytes up
// under the first row's hex column.
function buildFrameLines(line: SasDecodedLine): SasModelLine[] {
  const dir = dirLabel(line.direction);
  const role = dirRole(line.direction);
  const ts = (line.timestamp ?? '').padEnd(TS_WIDTH);
  const prefix = `${ts}  ${dir}  `;
  const indent = ' '.repeat(prefix.length);
  return wrapHexTokens(line.rawHex).map((row, i) => [frameGutter(), { text: `${i === 0 ? prefix : indent}${row}`, role }]);
}

/** Per-exchange column widths for field rows, computed once from every field
 * across every line in the exchange so sibling rows (e.g. the whole Features
 * block) share one alignment regardless of which line each field is on. */
type FieldColumns = { labelCol: number; valueCol: number; numericTotal: number };

function computeFieldColumns(fields: readonly SasDecodedField[]): FieldColumns {
  const bitRefFields = fields.filter((f) => isBitRef(f.rawHex));
  const numericFields = fields.filter((f) => !isBitRef(f.rawHex) && isNumericValue(f.value));

  const labelCol = Math.max(FIELD_LABEL_FLOOR, ...bitRefFields.map((f) => f.name.length + 2));
  const valueCol = Math.max(FIELD_VALUE_FLOOR, ...bitRefFields.map((f) => f.value.length + 2));
  const numericTotal = Math.max(NUMERIC_TOTAL_FLOOR, ...numericFields.map((f) => f.name.length + f.value.length + 1));

  return { labelCol, valueCol, numericTotal };
}

function buildFieldLine(field: SasDecodedField, columns: FieldColumns): SasModelLine {
  const bitRef = isBitRef(field.rawHex);
  const numeric = !bitRef && isNumericValue(field.value);
  const valueRole = fieldValueRole(field.value, numeric);

  if (numeric) {
    const label = field.name.padEnd(Math.max(columns.numericTotal - field.value.length, field.name.length));
    return [decodeGutter(), { text: label, role: 'text' }, { text: field.value, role: valueRole }];
  }

  if (bitRef) {
    const label = field.name.padEnd(columns.labelCol);
    const value = field.value.padEnd(columns.valueCol);
    return [decodeGutter(), { text: label, role: 'text' }, { text: value, role: valueRole }, { text: field.rawHex ?? '', role: 'dim' }];
  }

  // Non-numeric, no bit-ref (e.g. a raw-byte-plus-binary bitmap header): a
  // small fixed gap, no further column alignment — nothing follows it.
  return [decodeGutter(), { text: `${field.name}${' '.repeat(TEXT_FIELD_GAP)}`, role: 'text' }, { text: field.value, role: valueRole }];
}

// Summary line + CRC verdict, then one line per decoded field, then one line
// per warning not already folded into the CRC-BAD summary line above.
function buildDecodeLines(line: SasDecodedLine, columns: FieldColumns): SasModelLine[] {
  const out: SasModelLine[] = [];

  if (line.crc === 'Bad') {
    out.push([decodeGutter(), { text: `${line.summary}  — CRC BAD`, role: 'err' }]);
  } else {
    const summarySpans: SasModelLine = [decodeGutter(), { text: line.summary, role: 'text' }];
    if (line.crc === 'Ok') {
      // Only the verdict word itself is ok-role; "· CRC" stays general text.
      summarySpans.push({ text: ' · CRC ', role: 'text' }, { text: 'OK', role: 'ok' });
    } else if (line.crc === 'Missing') {
      summarySpans.push({ text: ' · CRC missing', role: 'text' });
    }
    out.push(summarySpans);
  }

  for (const field of line.fields) out.push(buildFieldLine(field, columns));

  for (const warning of line.warnings) {
    if (warning === 'CRC BAD') continue; // already reflected in the summary line above
    out.push([decodeGutter(), { text: warning, role: isErrorWarning(warning) ? 'err' : 'text' }]);
  }

  return out;
}

function exchangeTitle(exchange: SasExchange, lines: readonly SasDecodedLine[]): string {
  const first = lines[exchange.lineIndexes[0]];
  const parts = [exchange.title];
  if (first) {
    // Some summaries (e.g. General Poll) already embed "@ addr N" — don't repeat it.
    const addrText = `addr ${first.address}`;
    if (!exchange.title.includes(addrText)) parts.push(addrText);
  }
  return parts.join('  ·  ');
}

// "┌─ {title} " + fill dashes + "┐", total length == width.
function boxTop(title: string, width: number): SasModelLine {
  const dashes = Math.max(width - title.length - 5, 1);
  return [{ text: `┌─ ${title} ${'─'.repeat(dashes)}┐`, role: 'frame' }];
}

// "└" + fill dashes + "┘", total length == width.
function boxBottom(width: number): SasModelLine {
  return [{ text: `└${'─'.repeat(Math.max(width - 2, 1))}┘`, role: 'frame' }];
}

/** Inclusive [start, end] range of `buildLineModel`'s output indices spanned
 * by one exchange's box — its `┌─…─┐` top border through its `└─…─┘` bottom
 * border, NOT including the trailing blank spacer row before the next
 * exchange. Index `i` into the array `buildExchangeRanges` returns matches
 * index `i` into `result.exchanges` (== `SasCommandSearchOption.exchangeIndexes`
 * in `commandSearch.ts`). */
export type SasExchangeRange = { start: number; end: number };

// Shared by buildLineModel and buildExchangeRanges so the two can never
// drift apart — one pass builds both the rendered/exported line model AND
// the per-exchange row ranges the command-search nav (commandSearch.ts)
// uses to scroll/highlight a single exchange.
function buildLineModelWithRanges(result: SasDecodeResult): { lines: SasModelLine[]; ranges: SasExchangeRange[] } {
  const out: SasModelLine[] = [];
  const ranges: SasExchangeRange[] = [];

  for (const exchange of result.exchanges) {
    const exchangeLines = exchange.lineIndexes
      .map((idx) => result.lines[idx])
      .filter((l): l is SasDecodedLine => !!l);
    const columns = computeFieldColumns(exchangeLines.flatMap((l) => l.fields));

    const body: SasModelLine[] = [];
    exchangeLines.forEach((line, i) => {
      if (i > 0) body.push(innerSpacer());
      body.push(...buildFrameLines(line));
      body.push(...buildDecodeLines(line, columns));
    });

    const title = exchangeTitle(exchange, result.lines);
    const width = Math.max(MIN_BOX_WIDTH, title.length + 6, ...body.map(lineLength));

    const start = out.length;
    out.push(boxTop(title, width));
    out.push(...body);
    out.push(boxBottom(width));
    ranges.push({ start, end: out.length - 1 });
    out.push([]);
  }

  return { lines: out, ranges };
}

/**
 * Build the single line-model that both the on-screen decode stream and the
 * .txt export are built from — one code path, no possibility of divergence.
 * Screen render = each span coloured per its `role`; export
 * (`exportLineModelText`) = the same spans' text concatenated verbatim.
 *
 * One closed box per exchange (`┌─ … ─┐` / `└─…─┘`), titled with the command
 * name and address; each line inside as a raw TX/RX hex row followed by its
 * decode (summary + CRC verdict, then one row per field), with a blank `│`
 * spacer between consecutive lines' blocks (e.g. request vs. response); a
 * blank spacer row before the next box.
 */
export function buildLineModel(result: SasDecodeResult): SasModelLine[] {
  return buildLineModelWithRanges(result).lines;
}

/** For each exchange (by index into `result.exchanges`), the inclusive
 * model-line-index range its box spans in `buildLineModel`'s output — see
 * `SasExchangeRange`. Used by the command-search nav to scroll a matched
 * exchange into view and highlight its whole box without touching the line
 * model's text (highlighting is a `DecodeStream` view concern, keyed by
 * range, never a change to span text). */
export function buildExchangeRanges(result: SasDecodeResult): SasExchangeRange[] {
  return buildLineModelWithRanges(result).ranges;
}

/** Serialise a line model to plain text — the entire .txt export
 * implementation. Identical to what the stream renders, minus colour. */
export function exportLineModelText(lines: readonly SasModelLine[]): string {
  return lines.map((line) => line.map((span) => span.text).join('')).join('\n');
}
